// Was man über eine Person wissen muss, BEVOR man mit ihr spricht.
//
// Lilly, 25.08.2026, zwei Rückmeldungen aus derselben Wurzel:
//
//   „Es wäre gut, wenn wir den Kunden öffnen, das sehen könnten, was wir bei
//    der Wiedervorlage geschrieben haben, und auch den Zeitpunkt — da muss man
//    nicht zurückschalten."
//
//   „Alle Notizen aus dem alten System wurden nicht übernommen. Was vor allem
//    bei denen doof ist, wo der Qualicall und alles schon fertig war, aber die
//    erst nach den Ferien einen Termin ausmachen wollten."
//
// Beides ist längst gespeichert — es stand nur nicht dort, wo man es beim
// Telefonieren braucht. Die Wiedervorlage-Notiz lag ausschließlich in der
// Wiedervorlagen-Liste, die Alt-Notiz ausschließlich in der Kundenakte. Wer im
// Power Dialer telefoniert, sieht beides nicht und muss zurückschalten.
//
// Dieses Modul holt beides an einer Stelle, damit Kundenakte und Anruffenster
// dieselbe Quelle benutzen.

import { supabase } from './supabase';

export interface WiedervorlageKurz {
  id: string;
  due_at: string;
  note: string | null;
  /** pending | done | cancelled */
  status: string;
  /** null = offen für alle. Nötig, damit die Zuständigkeit auch in der
   *  Kundenakte umgehängt werden kann (nicht nur der Name zum Anzeigen). */
  agent_id: string | null;
  agent_name: string | null;
  completed_at: string | null;
  /** 'manual' = von einer Person gesetzt, 'system' = automatischer Reminder. */
  source: string | null;
  /** Wer die Wiedervorlage angelegt/zugewiesen hat — für „von X zugewiesen". */
  created_by: string | null;
  created_by_name: string | null;
  /** Dritter Status neben Erledigt/Abgebrochen: schon versucht, aber nicht
   *  direkt erreicht — bleibt offen, ist aber als „in Bearbeitung" markiert. */
  in_progress?: boolean | null;
}

export interface KundenKontext {
  /** Notiz aus der Zeit vor der Umstellung (crm.customers.notes). */
  altNotiz: string | null;
  wiedervorlagen: WiedervorlageKurz[];
}

const WV_SPALTEN =
  'id, due_at, note, status, agent_id, agent_name, completed_at, source, created_by, created_by_name, in_progress';

/**
 * Wiedervorlagen einer Person — offene zuerst, danach erledigte/abgebrochene,
 * jeweils die zeitlich nächsten. Fehler werfen NICHT: die Anzeige ist
 * Zusatzinformation und darf weder die Kundenakte noch ein laufendes Gespräch
 * mitreißen.
 */
export async function ladeWiedervorlagen(
  customerId: string,
  limit = 12,
): Promise<WiedervorlageKurz[]> {
  const { data, error } = await supabase
    .from('v_callbacks')
    .select(WV_SPALTEN)
    .eq('customer_id', customerId)
    .order('due_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return sortiereWiedervorlagen((data as WiedervorlageKurz[]) ?? []);
}

/** Alt-Notiz und Wiedervorlagen in einem Rutsch (fürs Anruffenster). */
export async function ladeKundenKontext(customerId: string): Promise<KundenKontext> {
  const [kunde, wv] = await Promise.all([
    supabase.from('v_callcenter_customers').select('notes').eq('customer_id', customerId).maybeSingle(),
    ladeWiedervorlagen(customerId),
  ]);
  const notes = (kunde.data as { notes?: string | null } | null)?.notes ?? null;
  return { altNotiz: kunde.error ? null : notes, wiedervorlagen: wv };
}

/** Offene zuerst (früheste Fälligkeit oben), danach die erledigten (neueste oben). */
export function sortiereWiedervorlagen(rows: WiedervorlageKurz[]): WiedervorlageKurz[] {
  const offen = rows.filter(istOffen).sort((a, b) => a.due_at.localeCompare(b.due_at));
  const erledigt = rows.filter((r) => !istOffen(r)).sort((a, b) => b.due_at.localeCompare(a.due_at));
  return [...offen, ...erledigt];
}

export function istOffen(w: WiedervorlageKurz): boolean {
  return w.status === 'pending';
}

export interface Faelligkeit {
  /** „heute, 10:00" · „in 3 Tagen · Fr, 29.08., 10:00" */
  text: string;
  /** Termin liegt in der Vergangenheit — in der Oberfläche hervorheben. */
  ueberfaellig: boolean;
  heute: boolean;
}

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/**
 * Fälligkeit in Klartext. Bewusst mit Kalendertagen gerechnet, nicht mit
 * 24-Stunden-Schritten: eine Wiedervorlage heute um 9:00, gesehen um 17:00, ist
 * „heute" und nicht „vor 8 Stunden".
 */
export function faelligkeit(dueIso: string, jetzt: Date = new Date()): Faelligkeit {
  const due = new Date(dueIso);
  if (isNaN(due.getTime())) return { text: dueIso, ueberfaellig: false, heute: false };

  const uhrzeit = due.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const tage = tagesAbstand(jetzt, due);
  const ueberfaellig = due.getTime() < jetzt.getTime();

  if (tage === 0) return { text: `heute, ${uhrzeit}`, ueberfaellig, heute: true };
  if (tage === 1) return { text: `morgen, ${uhrzeit}`, ueberfaellig: false, heute: false };
  if (tage === -1) return { text: `gestern, ${uhrzeit}`, ueberfaellig: true, heute: false };

  const datum = `${WOCHENTAG[due.getDay()]}, ${due.toLocaleDateString('de-DE')}, ${uhrzeit}`;
  if (tage > 1) return { text: `in ${tage} Tagen · ${datum}`, ueberfaellig: false, heute: false };
  return { text: `seit ${Math.abs(tage)} Tagen offen · ${datum}`, ueberfaellig: true, heute: false };
}

/** Ganze Kalendertage zwischen zwei Zeitpunkten (positiv = b liegt später). */
function tagesAbstand(a: Date, b: Date): number {
  const tagA = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const tagB = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((tagB - tagA) / 86_400_000);
}

/** Beschriftung für den Zustand einer Wiedervorlage. */
export function statusText(w: WiedervorlageKurz): string {
  switch (w.status) {
    case 'pending': return 'offen';
    case 'done': return 'erledigt';
    case 'cancelled': return 'abgebrochen';
    default: return w.status;
  }
}
